import './claim-entity.detail.contact.scss';

import {
  Address,
  AliasType,
  ContactType,
  Entity,
  EntityEdge,
  Person,
  PersonEdges,
  getPaginatedNodes,
  useRemoveVendorFromContactMutation,
  useSelectedVendorsAndContactListQuery,
  VendorType,
  ClaimantType,
  ClaimContactType,
} from '@converge/graphql';
import { Box, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';

import {
  ClaimantInitialData,
  ClaimEntityClaimantModal,
  initalAddressData,
} from './claim-entity.claimant.modal';
import { ClaimEntityPageContainer } from './claim-entity.page.container';
import { ClaimEntityVendorModal } from './claim-entity.vendor.modal';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { styled } from '@mui/material/styles';
import { useConvergeSnackBar } from './../../../contexts';
import { useOutletContext } from 'react-router-dom';
import { useState } from 'react';
import { capitalizeFirstLetterRemoveUnderLine } from '@converge/utils';
import { ClaimEntityDetailContactModal } from './claim-entity.detail.contact-modal';
import { ClaimEntityAdditionalInfoModal } from './claim-entity.additional-info.modal';
import { IProps } from './claim-entity.detail.overview';
import { UnifiedDeleteContactModal } from './unified-delete-contact-modal';

// Extend Entity type to include insuredEntity property
interface ExtendedEntity extends Entity {
  insuredEntity?: {
    node: {
      aliases?: {
        edges?: Array<{
          node: Alias;
        }>;
      };
      addresses?: {
        edges?: Array<{
          node: Address;
        }>;
      };
      persons?: {
        edges?: Array<{
          node: Person;
        }>;
      };
    };
  };
}

export interface claimEntityIProps {
  claimEntity: ExtendedEntity;
  claimUuid: string;
  claimSubmissionDetails?: {
    submissions?: {
      edges: Array<{
        node: {
          brokerEntity?: EntityEdge;
          insuredEntity: EntityEdge;
          persons?: PersonEdges;
        };
      }>;
    };
  };
}

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  backgroundColor: 'white',
  '&::before': {
    display: 'none',
  },
  marginBottom: '16px',
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} sx={{ flexDirection: 'row' }} />
))(({ theme }) => ({
  '& .MuiAccordionSummary-content': {
    margin: '8px 0',
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: '16px',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
}));

const ContactCard = styled(Box)(({ theme }) => ({
  border: '1px solid #666',
  borderRadius: '3px',
  backgroundColor: '#fff',
  position: 'relative',
}));

interface Alias {
  name?: string | null;
  primary?: boolean;
  type?: AliasType;
}

interface ContactData {
  type: ContactType;
  value: string;
  uuid?: string;
}

interface RegionNode {
  uuid: string;
  name: string;
}

interface CountryNode {
  uuid: string;
  name: string;
  iso2Code: string;
}

interface AddressData {
  street1: string;
  street2?: string;
  city: string;
  regionUuid?: string;
  postalCode: string;
  countryUuid?: string;
  uuid?: string;
  regions?: {
    edges?: Array<{
      node: RegionNode;
    }>;
  };
  countries?: {
    edges?: Array<{
      node: CountryNode;
    }>;
  };
}

// Type that matches the actual GraphQL response structure
interface ContactFromGraphQL {
  __typename?: 'ClaimContact' | undefined;
  uuid: string;
  firstName?: string | null | undefined;
  lastName?: string | null | undefined;
  type?: ClaimantType | null | undefined;
  companyName?: string | null | undefined;
  contacts?: ContactData[] | null | undefined;
  addresses?: AddressData[] | null | undefined;
  isInsured?: boolean | null | undefined;
}

// Original interface for internal use (keeping for backward compatibility)
interface ClaimantContact {
  uuid: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  type: ClaimantType;
  contacts?: ContactData[];
  addresses?: AddressData[];
  isInsured?: boolean;
}
interface DeleteContact {
  claimantUuid: string;
  type: string;
  claimUuid: string;
}

// eslint-disable-next-line react-hooks/rules-of-hooks

// Helper function to format phone number display
const formatPhoneDisplay = (phoneValue: string | null | undefined): string => {
  if (!phoneValue) return '';

  // If the phone value is just 'x' or empty, don't show anything
  if (phoneValue?.trim() === 'x' || phoneValue?.trim() === '') return '';

  let phoneNumber = phoneValue;
  let extension = '';

  // Handle extensions
  if (phoneValue.includes('x')) {
    const parts = phoneValue.split('x');
    phoneNumber = parts[0];
    extension = parts[1];

    // If there's nothing after 'x' or it's empty, ignore the extension
    if (!extension || extension?.trim() === '') {
      extension = '';
    }
  }

  // Clean the phone number - remove all non-digit characters except '+'
  const cleanPhone = phoneNumber.replace(/[^\d+]/g, '');

  // Format US phone numbers
  if (cleanPhone.startsWith('+1') && cleanPhone.length === 12) {
    // Format: +1(XXX)-XXX-XXXX
    const formatted = `+1 (${cleanPhone.slice(2, 5)}) ${cleanPhone.slice(
      5,
      8
    )}-${cleanPhone.slice(8, 12)}`;
    return extension ? `${formatted} ext ${extension?.trim()}` : formatted;
  } else if (cleanPhone.startsWith('1') && cleanPhone.length === 11) {
    // Format: +1(XXX)-XXX-XXXX
    const formatted = `+1 (${cleanPhone.slice(1, 4)}) ${cleanPhone.slice(
      4,
      7
    )}-${cleanPhone.slice(7, 11)}`;
    return extension ? `${formatted} ext ${extension?.trim()}` : formatted;
  } else if (cleanPhone.length === 10 && !cleanPhone.startsWith('+')) {
    // Format: +1(XXX)-XXX-XXXX
    const formatted = `+1 (${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(
      3,
      6
    )}-${cleanPhone.slice(6, 10)}`;
    return extension ? `${formatted} ext ${extension?.trim()}` : formatted;
  }

  // For international numbers or other formats, return as-is with extension if present
  return extension ? `${phoneNumber} ext ${extension?.trim()}` : phoneNumber;
};

export const ClaimEntityDetailContact = () => {
  const { claimEntity, claimSubmissionDetails } = useOutletContext<IProps>();

  const [isInsuredOpen, setInsuredOpen] = useState(true);
  const [isClaimantOpen, setClaimantOpen] = useState(true);
  const [isVendorsOpen, setVendorsOpen] = useState(true);
  const [isBrokeragesOpen, setBrokeragesOpen] = useState(false);
  // const [isInsuranceOpen, setInsuranceOpen] = useState(false);
  const [isOthersOpen, setOthersOpen] = useState(false);
  const [isVenderModalOpen, setIsVenderModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [typeOfContact, setTypeOfContact] = useState<ClaimantType>();
  const [selectedContactForEdit, setSelectedContactForEdit] = useState<
    ClaimantInitialData | undefined
  >();
  const [selectedContactForDelete, setSelectedContactForDelete] = useState<
    DeleteContact | undefined
  >();

  const [selectedAdditionalInfoForDelete, setSelectedAdditionalInfoForDelete] =
    useState<
      | {
          uuid: string;
        }
      | undefined
    >();
  const [isDeleteForContactOpen, setIsDeleteForContactOpen] = useState(false);
  const [isDeleteForadditionalOpen, setIsDeleteForadditionalOpen] =
    useState(false);
  const [
    isClaimEntityDetailsContactModalOpen,
    setIsClaimEntityDetailsContactModalOpen,
  ] = useState(false);
  const [selectedVendorForDelete, setSelectedVendorForDelete] = useState<{
    uuid: string;
    serviceUuid: string;
    hasReservePayment: boolean;
  } | null>(null);
  const [isAdditionalInfoModalOpen, setIsAdditionalInfoModalOpen] =
    useState(false);
  const [selectedEntityForAdditionalInfo, setSelectedEntityForAdditionalInfo] =
    useState<{ uuid: string; type: ClaimContactType } | null>(null);
  const [
    selectedContactForAdditionalEdit,
    setSelectedContactForAdditionalEdit,
  ] = useState<{
    uuid: string;
    targetUuid: string;
    claimUuid: string;
    type: ClaimContactType;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  } | null>(null);
  const { notify } = useConvergeSnackBar();
  const extractClaimUuidFromUrl = (
    url: string = window.location.pathname
  ): string => {
    const match = url.match(/\/claims\/([^/]+)/);
    return match ? match[1] : '';
  };
  const claimUuid = extractClaimUuidFromUrl();
  const {
    vendors,
    contacts,
    insuredEntity: insuredEntityFromQuery,
    brokerEntity: brokerEntityFromQuery,
    refetch,
  } = useSelectedVendorsAndContactListQuery(claimUuid);

  // Define types for mapping vendor data

  const claimantsList = contacts.filter(
    (claimants) => claimants.type === ClaimantType.Claimant
  );
  const otherContact = contacts.filter(
    (contact) => contact.type === ClaimantType.ClaimContact
  );

  const handleCloseModal = () => {
    setIsVenderModalOpen(false);
  };
  const handleCloseDeleteModal = () => {
    setIsDeleteForContactOpen(false);
  };
  const handleCloseDeleteAdditionalModal = () => {
    setIsDeleteForadditionalOpen(false);
    setSelectedAdditionalInfoForDelete(undefined);
  };
  const handleCloseClaimantModal = () => {
    setIsContactModalOpen(false);
    setSelectedContactForEdit(undefined);
  };

  const closeClaimEntityDetailsContactModal = () => {
    setIsClaimEntityDetailsContactModalOpen(false);
    // Use setTimeout to reset vendor data after modal starts closing
    setTimeout(() => setSelectedVendorForDelete(null), 100);
  };

  const handleVendorDelete = (vendor: VendorType) => {
    const vendorData = {
      uuid: vendor?.uuid || '',
      serviceUuid: vendor?.services?.edges?.[0]?.node?.uuid || '',
      hasReservePayment: vendor.hasReservePayment === true,
    };
    setSelectedVendorForDelete(vendorData);
    setIsClaimEntityDetailsContactModalOpen(true);
  };

  // Generic function to map contact data to ClaimantInitialData format
  // Handles both nullable GraphQL types and internal ClaimantContact types
  const mapContactToClaimantData = (
    contact: ContactFromGraphQL | ClaimantContact
  ): ClaimantInitialData => {
    return {
      uuid: contact.uuid,
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      companyName: contact.companyName || '',
      contacts:
        contact.contacts?.map((c: ContactData) => ({
          type: c.type,
          value: c.value,
          uuid: c.uuid,
          contactUuid: c.uuid, // Map to contactUuid for edit mode
        })) || [],
      addresses:
        contact.addresses?.map(
          (addr: AddressData): initalAddressData => ({
            street1: addr.street1,
            street2: addr.street2,
            city: addr.city,
            regionUuid:
              addr.regionUuid || addr?.regions?.edges?.[0]?.node?.uuid || '',
            postalCode: addr.postalCode,
            countryUuid:
              addr.countryUuid || addr?.countries?.edges?.[0]?.node?.uuid || '',
            uuid: addr.uuid,
            addressUuid: addr.uuid, // Map to addressUuid for edit mode
          })
        ) || [],
      isInsured: contact.isInsured || false,
    };
  };

  // Specific handlers that use the generic mapping function
  const handleEditContact = (contact: ClaimantContact, type: ClaimantType) => {
    const mappedData = mapContactToClaimantData(contact);
    setSelectedContactForEdit(mappedData);
    setIsContactModalOpen(true);
    setTypeOfContact(type);
  };

  const handleEditAdditional = (
    contact: {
      uuid?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      targetUuid?: string | null;
      contacts?: Array<{
        type?: ContactType | null;
        value?: string | null;
      }> | null;
    },
    claimUuid: string,
    type: ClaimContactType
  ) => {
    // Extract email and phone from contacts array
    const email =
      contact.contacts?.find((c) => c?.type === ContactType.Email)?.value || '';
    const phone =
      contact.contacts?.find((c) => c?.type === ContactType.Phone)?.value || '';

    setSelectedContactForAdditionalEdit({
      uuid: contact.uuid || '',
      claimUuid,
      targetUuid: contact.targetUuid || '',
      type: type,
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      email,
      phone,
    });
    setIsAdditionalInfoModalOpen(true);
  };

  const handleDeleteClaimant = (claimantUuid: string, type: string) => {
    setSelectedContactForDelete({
      claimantUuid: claimantUuid,
      claimUuid: claimUuid,
      type: type,
    });
    setIsDeleteForContactOpen(true);
  };

  const handleDeleteAdditionalInfo = (uuid: string) => {
    setSelectedAdditionalInfoForDelete({ uuid });
    setIsDeleteForadditionalOpen(true);
  };

  // Extract submission details
  const { insuredEntity: contactInsuredEntity, persons } =
    claimSubmissionDetails?.submissions?.edges[0]?.node || {};

  // Use brokerEntity from GET_SELECTED_VENDOR_LIST only
  const brokerEntity = brokerEntityFromQuery;

  console.log('brokerEntity (from GET_SELECTED_VENDOR_LIST):', brokerEntity);

  const insuredPersonName =
    contactInsuredEntity?.node?.persons?.edges[0]?.node?.firstName;
  const insuredPersonLastName =
    contactInsuredEntity?.node?.persons?.edges[0]?.node?.lastName;
  const insuredPersonPhone =
    contactInsuredEntity?.node?.persons?.edges[0]?.node.contacts?.edges?.find(
      (contact) => contact.node.type === ContactType.Phone
    )?.node?.value;
  const insuredPersonEmail =
    contactInsuredEntity?.node?.persons?.edges[0]?.node?.contacts?.edges?.find(
      (contact) => contact.node.type === ContactType.Email
    )?.node?.value;

  // Get brokerage primary Address and name
  const brokerageName = brokerEntity?.node?.aliases?.edges.filter(
    (brokerageName) => brokerageName.node.type === 'LEGAL_NAME'
  )[0]?.node?.name;
  const brokerageAddress = brokerEntity?.node?.addresses.edges[0].node;

  const primaryBroker = persons?.edges.filter(
    (item) => item.node.personType?.type === 'AGENCY_CONTACT'
  )[0]?.node;
  const primaryBrokerEmail = primaryBroker?.contacts?.edges?.find(
    (contact) => contact.node.type === ContactType.Email
  )?.node?.value;

  const primaryBrokerPhone = primaryBroker?.contacts?.edges?.find(
    (contact) => contact.node.type === ContactType.Phone
  )?.node?.value;

  // Use insuredEntity from vendors query instead of claimEntity
  const insuredEntity =
    insuredEntityFromQuery?.node || claimEntity?.insuredEntity?.node;

  const insuredAliases =
    (getPaginatedNodes(insuredEntity?.aliases) as Alias[]) || [];
  const insuredAddresses =
    (getPaginatedNodes(insuredEntity?.addresses) as Address[]) || [];

  // Get primary insured name
  const insuredName =
    insuredAliases?.find(
      (alias: Alias) => alias?.primary && alias?.type === AliasType.LegalName
    )?.name ||
    insuredAliases?.[0]?.name ||
    'Unknown Insured';

  const additionalContactList = insuredEntity?.claimContacts?.edges;

  // Get primary insured address
  const primaryInsuredAddress =
    insuredAddresses?.filter(
      (address: Address) => address.primary && address.source === 'PRIMARY'
    )[0] ??
    insuredAddresses?.filter(
      (address: Address) => address.source === 'CONVERGE'
    )[0];

  const handleInsuredAccordion = (
    _: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setInsuredOpen(newExpanded);
  };

  const handleCalimantAccordion = (
    _: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setClaimantOpen(newExpanded);
  };

  const handleVendorsAccordion = (
    _: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setVendorsOpen(newExpanded);
  };

  const handleBrokeragesAccordion = (
    _: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setBrokeragesOpen(newExpanded);
  };

  // const handleInsuranceAccordion = (
  //   _: React.SyntheticEvent,
  //   newExpanded: boolean
  // ) => {
  //   setInsuranceOpen(newExpanded);
  // };

  const handleOthersAccordion = (
    _: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setOthersOpen(newExpanded);
  };

  const { removeVendorFromContact } = useRemoveVendorFromContactMutation();
  const handleDelete = async (vendorUuid: string, serviceUuid: string) => {
    try {
      await removeVendorFromContact({
        claimUuid,
        vendorUuid: vendorUuid,
        serviceUuid: serviceUuid,
      });

      notify(`Vendor deleted successfully`, {
        variant: 'success',
      });
      refetch();
    } catch (error) {
      console.error('Error deleting vendor:', error);
      notify('Error deleting vendor', { variant: 'error' });
    }
  };

  return (
    <>
      <ClaimEntityClaimantModal
        titleName={
          selectedContactForEdit
            ? `Edit ${
                typeOfContact === ClaimantType.ClaimContact
                  ? 'Additional Contact'
                  : typeOfContact &&
                    capitalizeFirstLetterRemoveUnderLine(typeOfContact)
              }`
            : `Add ${
                typeOfContact === ClaimantType.ClaimContact
                  ? 'Additional Contact'
                  : typeOfContact &&
                    capitalizeFirstLetterRemoveUnderLine(typeOfContact)
              }`
        }
        open={isContactModalOpen}
        onClose={handleCloseClaimantModal}
        type={typeOfContact}
        claimUuid={claimUuid}
        editMode={!!selectedContactForEdit}
        initialData={selectedContactForEdit}
        insuredData={{
          companyName: insuredName,
          firstName: insuredPersonName,
          lastName: insuredPersonLastName,
          email: insuredPersonEmail,
          phone: insuredPersonPhone,
          address: {
            street1: primaryInsuredAddress?.street1 ?? '',
            street2: primaryInsuredAddress?.street2 ?? '',
            city: primaryInsuredAddress?.city ?? '',
            regionUuid:
              getPaginatedNodes(primaryInsuredAddress?.regions)?.[0]?.uuid ??
              '',
            postalCode: primaryInsuredAddress?.postalCode ?? '',
            countryUuid:
              getPaginatedNodes(primaryInsuredAddress?.countries)?.[0]?.uuid ??
              '',
          },
        }}
        onNoteSaved={() => {
          refetch();
        }}
      />

      <UnifiedDeleteContactModal
        open={isDeleteForadditionalOpen || isDeleteForContactOpen}
        onClose={
          isDeleteForadditionalOpen
            ? handleCloseDeleteAdditionalModal
            : handleCloseDeleteModal
        }
        mode={isDeleteForadditionalOpen ? 'additionalInfo' : 'contact'}
        contactData={selectedContactForDelete}
        titleName={
          selectedContactForDelete?.type
            ? `Delete ${selectedContactForDelete.type}`
            : 'Delete Contact'
        }
        additionalInfoData={selectedAdditionalInfoForDelete}
        onNoteSaved={() => {
          refetch();
        }}
      />

      <ClaimEntityVendorModal
        open={isVenderModalOpen}
        onClose={handleCloseModal}
        onNoteSaved={() => {
          refetch();
        }}
      />

      <ClaimEntityDetailContactModal
        open={isClaimEntityDetailsContactModalOpen}
        onClose={closeClaimEntityDetailsContactModal}
        submissionUuid={claimUuid}
        title={
          selectedVendorForDelete?.hasReservePayment
            ? 'Vendor Has Already Been Paid'
            : 'Delete Vendor'
        }
        mainMessage={
          selectedVendorForDelete?.hasReservePayment
            ? 'This vendor has already been paid on this claim. You cannot delete the vendor because it must remain associated with this claim.'
            : 'Are you sure you want to delete this vendor from this claim? You can always add it back if necessary.'
        }
        isdeleteable={!selectedVendorForDelete?.hasReservePayment}
        cancelLabel={
          selectedVendorForDelete?.hasReservePayment ? 'GO BACK' : 'CANCEL'
        }
        handelDelete={
          selectedVendorForDelete && !selectedVendorForDelete.hasReservePayment
            ? () =>
                handleDelete(
                  selectedVendorForDelete.uuid,
                  selectedVendorForDelete.serviceUuid
                )
            : undefined
        }
      />
      <ClaimEntityAdditionalInfoModal
        open={isAdditionalInfoModalOpen}
        onClose={() => {
          setIsAdditionalInfoModalOpen(false);
          setSelectedEntityForAdditionalInfo(null);
          setSelectedContactForAdditionalEdit(null);
        }}
        claimUuid={selectedContactForAdditionalEdit?.claimUuid || claimUuid}
        TargetUuid={
          selectedContactForAdditionalEdit?.targetUuid ||
          selectedEntityForAdditionalInfo?.uuid ||
          insuredEntity?.uuid ||
          ''
        }
        type={
          selectedContactForAdditionalEdit?.type ||
          selectedEntityForAdditionalInfo?.type ||
          ClaimContactType.Insured
        }
        uuid={selectedContactForAdditionalEdit?.uuid}
        initialData={
          selectedContactForAdditionalEdit
            ? {
                firstName: selectedContactForAdditionalEdit.firstName,
                lastName: selectedContactForAdditionalEdit.lastName,
                email: selectedContactForAdditionalEdit.email,
                phone: selectedContactForAdditionalEdit.phone,
              }
            : undefined
        }
        onSaved={() => {
          refetch();
        }}
      />
      <ClaimEntityPageContainer
        title={'Contacts'}
        description=" All involved parties are listed here. Add additional vendors or claimants as necessary to complete this claim"
      >
        {/* INSURED AND CLAIMANTS Section */}
        <Accordion expanded={isInsuredOpen} onChange={handleInsuredAccordion}>
          <AccordionSummary>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isInsuredOpen ? (
                <KeyboardArrowDownIcon />
              ) : (
                <KeyboardArrowRightIcon />
              )}
              <Typography variant="h5" className="box-title">
                INSURED
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails className="accordion-details-style insured-accordion-container">
            <Box className="insured-cards-wrapper">
              <ContactCard className="contact-card-style left-card">
                <Box className="contact-card-header">INSURED INFORMATION</Box>
                <Typography variant="h6" className="card-title">
                  {insuredName}
                </Typography>
                <Typography variant="h6" className="card-title2">
                  {insuredPersonName} {insuredPersonLastName}
                </Typography>
                <Typography variant="h6" className="card-title2">
                  {primaryInsuredAddress?.street1}
                  {primaryInsuredAddress?.street2 &&
                    `, ${primaryInsuredAddress?.street2}`}
                </Typography>
                <Typography variant="h6" className="card-title2">
                  {primaryInsuredAddress?.city},{' '}
                  {getPaginatedNodes(primaryInsuredAddress?.regions)?.[0]
                    ?.iso2Code?.split('-')
                    .pop()}
                  , {primaryInsuredAddress?.postalCode},{' '}
                  {
                    getPaginatedNodes(primaryInsuredAddress?.countries)?.[0]
                      ?.iso2Code
                  }
                </Typography>
                <Typography variant="h6" className="card-title ">
                  {insuredPersonEmail}
                </Typography>
                <Typography variant="h6" className="card-title2">
                  {formatPhoneDisplay(insuredPersonPhone)}
                </Typography>
              </ContactCard>
              <ContactCard className="contact-card-style right-card">
                <Box className="contact-card-header">
                  Additional Information
                  <Box className="additiona-Info-contact-page">
                    {additionalContactList?.map((edge) => (
                      <>
                        <Box key={edge.node.uuid}>
                          <Box>
                            {edge.node.firstName} {edge.node.lastName}
                          </Box>
                          <Box>
                            {edge.node.contacts?.find(
                              (contact) => contact?.type === ContactType.Email
                            )?.value || ''}
                          </Box>
                          <Box>
                            {formatPhoneDisplay(
                              edge.node.contacts?.find(
                                (contact) => contact?.type === ContactType.Phone
                              )?.value || ''
                            )}
                          </Box>
                        </Box>
                        <Box className="edit-delete-container-additional">
                          <Box
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              handleEditAdditional(
                                edge.node,
                                claimUuid,
                                ClaimContactType.Insured
                              );
                            }}
                          >
                            EDIT
                          </Box>
                          <Box
                            style={{ cursor: 'pointer', marginLeft: '10px' }}
                            onClick={() => {
                              if (edge.node.uuid) {
                                handleDeleteAdditionalInfo(edge.node.uuid);
                              }
                            }}
                          >
                            DELETE
                          </Box>
                        </Box>
                      </>
                    ))}
                  </Box>
                </Box>
                <Box
                  className="add-information"
                  onClick={() => {
                    setSelectedEntityForAdditionalInfo({
                      uuid: insuredEntity?.uuid || '',
                      type: ClaimContactType.Insured,
                    });
                    setIsAdditionalInfoModalOpen(true);
                  }}
                >
                  <AddCircleOutlineIcon />

                  <Typography variant="h6" className="add-text-btn">
                    ADD ADDITIONAL INFORMATION
                  </Typography>
                </Box>
              </ContactCard>
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion expanded={isClaimantOpen} onChange={handleCalimantAccordion}>
          <AccordionSummary>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isClaimantOpen ? (
                <KeyboardArrowDownIcon />
              ) : (
                <KeyboardArrowRightIcon />
              )}
              <Typography variant="h5" className="box-title">
                CLAIMANT(S)
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails className="accordion-details-style insured-accordion-container">
            {claimantsList && claimantsList.length > 0 ? (
              claimantsList.map((contact, index) => (
                <Box
                  className="insured-cards-wrapper"
                  style={{ marginBottom: '20px' }}
                >
                  <ContactCard className="contact-card-style left-card">
                    <Box className="contact-card-header">
                      Claimant Information
                    </Box>
                    <Typography variant="h6" className="card-title">
                      {contact.companyName || ''}
                    </Typography>
                    <Typography variant="h6" className="card-title2">
                      {contact.firstName || ''} {contact.lastName || ''}
                    </Typography>

                    {contact?.addresses &&
                    contact?.addresses[0]?.street1?.trim() ? (
                      <>
                        <Typography variant="h6" className="card-title2">
                          {contact?.addresses &&
                            contact?.addresses[0]?.street1?.trim()}
                          {contact?.addresses && contact?.addresses[0]?.street2
                            ? `, ${
                                contact?.addresses &&
                                contact?.addresses[0]?.street2
                              }`
                            : ''}
                        </Typography>
                        <Typography variant="h6" className="card-title2">
                          {contact?.addresses && contact?.addresses[0]?.city
                            ? `${
                                contact?.addresses &&
                                contact?.addresses[0]?.city
                              }, `
                            : ''}
                          {contact?.addresses &&
                          contact?.addresses[0]?.regions?.edges?.[0]?.node
                            ?.iso2Code
                            ? `${contact?.addresses[0]?.regions?.edges?.[0]?.node?.iso2Code
                                ?.split('-')
                                .pop()}, `
                            : ''}

                          {contact?.addresses &&
                          contact?.addresses[0]?.postalCode
                            ? `${contact?.addresses[0]?.postalCode},  `
                            : ''}
                          {contact?.addresses &&
                          (contact?.addresses[0]?.postalCode ||
                            contact?.addresses[0]?.city ||
                            contact?.addresses[0]?.street2 ||
                            contact?.addresses[0]?.street1 ||
                            contact?.addresses[0]?.regions?.edges?.[0]?.node
                              ?.iso2Code)
                            ? `${contact?.addresses[0]?.countries?.edges?.[0]?.node?.iso2Code}`
                            : ''}
                        </Typography>
                      </>
                    ) : (
                      <p className="message-claimant">
                        Please enter an address for this claimant before making
                        a payment.
                      </p>
                    )}

                    <>
                      <Typography variant="h6" className="card-title">
                        {contact?.contacts?.find(
                          (contact) => contact?.type === ContactType.Email
                        )?.value || ''}
                      </Typography>
                      <Typography variant="h6" className="card-title2">
                        {formatPhoneDisplay(
                          contact?.contacts?.find(
                            (contact) => contact?.type === ContactType.Phone
                          )?.value
                        )}
                      </Typography>
                    </>
                    {contact.hasReservePayment === false && (
                      <Box className="edit-delete-container">
                        <Box
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            handleEditContact(
                              contact as ClaimantContact,
                              ClaimantType.Claimant as ClaimantType
                            );
                          }}
                        >
                          EDIT
                        </Box>
                        <Box
                          style={{ cursor: 'pointer', marginLeft: '10px' }}
                          onClick={() =>
                            handleDeleteClaimant(
                              contact.uuid as string,
                              'Claimant'
                            )
                          }
                        >
                          DELETE
                        </Box>
                      </Box>
                    )}
                  </ContactCard>
                  <ContactCard className="contact-card-style right-card">
                    <Box className="contact-card-header">
                      Additional Information
                      <Box className="additiona-Info-contact-page">
                        {contact.claimContacts?.map((edge) => (
                          <>
                            <Box key={edge.uuid}>
                              <Box>
                                {edge.firstName} {edge.lastName}
                              </Box>
                              <Box>
                                {edge.contacts?.find(
                                  (contact) =>
                                    contact?.type === ContactType.Email
                                )?.value || ''}
                              </Box>
                              <Box>
                                {formatPhoneDisplay(
                                  edge.contacts?.find(
                                    (contact) =>
                                      contact?.type === ContactType.Phone
                                  )?.value || ''
                                )}
                              </Box>
                            </Box>
                            <Box className="edit-delete-container-additional">
                              <Box
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  handleEditAdditional(
                                    edge,
                                    claimUuid,
                                    ClaimContactType.Claimant
                                  );
                                }}
                              >
                                EDIT
                              </Box>
                              <Box
                                style={{
                                  cursor: 'pointer',
                                  marginLeft: '10px',
                                }}
                                onClick={() => {
                                  if (edge.uuid) {
                                    handleDeleteAdditionalInfo(edge.uuid);
                                  }
                                }}
                              >
                                DELETE
                              </Box>
                            </Box>
                          </>
                        ))}
                      </Box>
                    </Box>
                    <Box
                      className="add-information"
                      onClick={() => {
                        setSelectedEntityForAdditionalInfo({
                          uuid: contact.uuid || '',
                          type: ClaimContactType.Claimant,
                        });
                        setIsAdditionalInfoModalOpen(true);
                      }}
                    >
                      <AddCircleOutlineIcon />

                      <Typography variant="h6" className="add-text-btn">
                        ADD ADDITIONAL INFORMATION
                      </Typography>
                    </Box>
                  </ContactCard>
                </Box>
              ))
            ) : (
              <Box
                className="insured-cards-wrapper"
                style={{ marginBottom: '20px' }}
              >
                <ContactCard className="contact-card-style left-card">
                  <Box className="contact-card-header">
                    Claimant Information
                  </Box>
                  <Box className="no-claimant-vendor">
                    No claimants have been added
                  </Box>
                </ContactCard>
                <ContactCard className="contact-card-style right-card">
                  <Box
                    className="contact-card-header"
                    style={{ color: 'grey' }}
                  >
                    Additional Information
                  </Box>
                  <Box className="add-information">
                    <AddCircleOutlineIcon style={{ color: 'grey' }} />

                    <Typography
                      variant="h6"
                      className="add-text-btn"
                      style={{ color: 'grey' }}
                    >
                      ADD ADDITIONAL INFORMATION
                    </Typography>
                  </Box>
                </ContactCard>
              </Box>
            )}

            <Box
              className="add-card"
              style={{ marginLeft: '10px' }}
              onClick={() => {
                setIsContactModalOpen(true);
                setTypeOfContact(ClaimantType.Claimant);
              }}
              sx={{
                cursor: 'pointer',
                color: 'black',
                pointerEvents: 'auto',
              }}
            >
              <AddCircleOutlineIcon />
              <Typography variant="h6" className="add-text-btn">
                ADD CLAIMANT
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* VENDORS Section */}
        <Accordion expanded={isVendorsOpen} onChange={handleVendorsAccordion}>
          <AccordionSummary>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isVendorsOpen ? (
                <KeyboardArrowDownIcon />
              ) : (
                <KeyboardArrowRightIcon />
              )}

              <Typography variant="h5" className="box-title">
                VENDOR(S)
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails className="accordion-details-style insured-accordion-container">
            {Array.isArray(vendors) && vendors.length > 0 ? (
              vendors.map((vendor, index) => (
                <Box
                  className="insured-cards-wrapper"
                  style={{ marginBottom: '20px' }}
                >
                  <ContactCard className="contact-card-style left-card">
                    <Box className="contact-card-header">
                      {' '}
                      {vendor?.services?.edges?.[0]?.node?.name || 'Vendor'}
                    </Box>
                    <Typography variant="h6" className="card-title">
                      {vendor?.aliases?.edges?.[0]?.node?.name || ''}
                    </Typography>
                    {vendor?.addresses?.edges?.[0]?.node && (
                      <>
                        <Typography variant="h6" className="card-title">
                          {vendor?.addresses?.edges?.[0]?.node?.street1 &&
                            vendor?.addresses?.edges?.[0]?.node?.street1?.trim()}
                          {vendor?.addresses?.edges?.[0]?.node?.street2
                            ? `, ${vendor?.addresses?.edges?.[0]?.node?.street2}`
                            : ''}
                        </Typography>
                        <Typography variant="h6" className="card-title">
                          {vendor?.addresses?.edges?.[0]?.node?.city
                            ? `${vendor?.addresses?.edges?.[0]?.node?.city}, `
                            : ''}
                          {vendor?.addresses &&
                          vendor?.addresses?.edges?.[0]?.node?.regions
                            ?.edges?.[0]?.node?.iso2Code
                            ? `${vendor?.addresses?.edges?.[0]?.node?.regions?.edges?.[0]?.node?.iso2Code
                                ?.split('-')
                                .pop()}, `
                            : ''}

                          {vendor?.addresses?.edges?.[0]?.node?.postalCode
                            ? `${vendor?.addresses?.edges?.[0]?.node?.postalCode},  `
                            : ''}
                          {vendor?.addresses?.edges?.[0]?.node?.countries
                            ?.edges?.[0]?.node?.iso2Code
                            ? `${vendor?.addresses?.edges?.[0]?.node?.countries?.edges?.[0]?.node?.iso2Code} `
                            : ''}
                        </Typography>
                      </>
                    )}

                    {vendor?.persons?.edges?.[0]?.node && (
                      <>
                        <Typography variant="h6" className="card-title">
                          {`${
                            vendor?.persons?.edges?.[0]?.node?.firstName || ''
                          } ${
                            vendor?.persons?.edges?.[0]?.node?.lastName || ''
                          }`}
                        </Typography>
                        <Typography variant="h6" className="card-data">
                          {vendor?.persons?.edges?.[0]?.node?.contacts?.edges?.find(
                            (contact) =>
                              contact?.node?.type === ContactType.Email
                          )?.node?.value || ''}
                        </Typography>
                        <Typography
                          variant="h6"
                          className="card-data last-item-card"
                          style={{ paddingBottom: '30px' }}
                        >
                          {formatPhoneDisplay(
                            vendor?.persons?.edges?.[0]?.node?.contacts?.edges?.find(
                              (contact) =>
                                contact?.node?.type === ContactType.Phone
                            )?.node?.value
                          )}
                        </Typography>
                      </>
                    )}

                    <Box
                      className="edit-delete-container"
                      style={{ marginLeft: '30px' }}
                    >
                      <Box
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleVendorDelete(vendor)}
                      >
                        DELETE
                      </Box>
                    </Box>
                  </ContactCard>
                  <ContactCard className="contact-card-style right-card">
                    <Box className="contact-card-header">
                      Additional Information
                      <Box className="additiona-Info-contact-page">
                        {vendor.claimContacts.edges?.map((edge) => (
                          <>
                            <Box key={edge.node.uuid}>
                              <Box>
                                {edge.node.firstName} {edge.node.lastName}
                              </Box>
                              <Box>
                                {edge.node.contacts?.find(
                                  (contact) =>
                                    contact?.type === ContactType.Email
                                )?.value || ''}
                              </Box>
                              <Box>
                                {formatPhoneDisplay(
                                  edge.node.contacts?.find(
                                    (contact) =>
                                      contact?.type === ContactType.Phone
                                  )?.value || ''
                                )}
                              </Box>
                            </Box>
                            <Box className="edit-delete-container-additional">
                              <Box
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  handleEditAdditional(
                                    edge.node,
                                    claimUuid,
                                    ClaimContactType.Vendor
                                  );
                                }}
                              >
                                EDIT
                              </Box>
                              <Box
                                style={{
                                  cursor: 'pointer',
                                  marginLeft: '10px',
                                }}
                                onClick={() => {
                                  if (edge.node.uuid) {
                                    handleDeleteAdditionalInfo(edge.node.uuid);
                                  }
                                }}
                              >
                                DELETE
                              </Box>
                            </Box>
                          </>
                        ))}
                      </Box>
                    </Box>
                    <Box
                      className="add-information"
                      onClick={() => {
                        setSelectedEntityForAdditionalInfo({
                          uuid: vendor?.uuid || '',
                          type: ClaimContactType.Vendor,
                        });
                        setIsAdditionalInfoModalOpen(true);
                      }}
                    >
                      <AddCircleOutlineIcon />

                      <Typography variant="h6" className="add-text-btn">
                        ADD ADDITIONAL INFORMATION
                      </Typography>
                    </Box>
                  </ContactCard>
                </Box>
              ))
            ) : (
              <Box
                className="insured-cards-wrapper"
                style={{ marginBottom: '20px' }}
              >
                <ContactCard className="contact-card-style left-card">
                  <Box className="contact-card-header">VENDOR CATEGORY</Box>

                  <Box className="no-claimant-vendor">
                    No vendors have been added
                  </Box>
                </ContactCard>
                <ContactCard className="contact-card-style right-card">
                  <Box
                    className="contact-card-header"
                    style={{ color: 'grey' }}
                  >
                    Additional Information
                  </Box>
                  <Box className="add-information">
                    <AddCircleOutlineIcon style={{ color: 'grey' }} />

                    <Typography
                      variant="h6"
                      className="add-text-btn"
                      style={{ color: 'grey' }}
                    >
                      ADD ADDITIONAL INFORMATION
                    </Typography>
                  </Box>
                </ContactCard>
              </Box>
            )}

            <Box
              className="add-card"
              style={{ marginLeft: '10px', marginTop: '20px' }}
              onClick={() => {
                setIsVenderModalOpen(true);
              }}
              sx={{
                cursor: 'pointer',
                color: 'black',
                pointerEvents: 'auto',
              }}
            >
              <AddCircleOutlineIcon />
              <Typography variant="h6" className="add-text-btn">
                ADD VENDOR(S)
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* BROKERAGES AND BROKERS Section */}
        <Accordion
          expanded={isBrokeragesOpen}
          onChange={handleBrokeragesAccordion}
        >
          <AccordionSummary>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isBrokeragesOpen ? (
                <KeyboardArrowDownIcon />
              ) : (
                <KeyboardArrowRightIcon />
              )}
              <Typography variant="h5" className="box-title">
                BROKERAGE AND BROKER
              </Typography>
            </Box>
          </AccordionSummary>

          <AccordionDetails className="accordion-details-style insured-accordion-container">
            <Box className="insured-cards-wrapper">
              <ContactCard className="contact-card-style left-card">
                <Typography variant="h6" className="contact-card-header">
                  BROKERAGE
                </Typography>
                <Typography variant="h6" className="card-title">
                  {brokerageName}
                </Typography>
                <Typography variant="h6" className="card-title">
                  {brokerageAddress?.street1}, {brokerageAddress?.street2}
                </Typography>
                <Typography variant="h6" className="card-title">
                  {brokerageAddress?.city},{' '}
                  {getPaginatedNodes(brokerageAddress?.regions)?.[0]
                    ?.iso2Code?.split('-')
                    .pop()}
                  , {brokerageAddress?.postalCode},{' '}
                  {
                    getPaginatedNodes(brokerageAddress?.countries)?.[0]
                      ?.iso2Code
                  }
                </Typography>
                <Typography variant="h6" className="contact-card-header">
                  BROKER
                </Typography>

                <Typography variant="h6" className="card-title">
                  {primaryBroker?.firstName ?? ''}{' '}
                  {primaryBroker?.lastName ?? ''}{' '}
                </Typography>
                <Typography variant="h6" className="card-title">
                  {primaryBrokerEmail || ''}
                </Typography>
                <Typography
                  variant="h6"
                  className="card-title"
                  style={{ marginBottom: '20px' }}
                >
                  {formatPhoneDisplay(primaryBrokerPhone)}
                </Typography>
              </ContactCard>
              <ContactCard className="contact-card-style right-card">
                <Box className="contact-card-header">
                  Additional Information
                  <Box className="additiona-Info-contact-page">
                    {brokerEntity?.node.claimContacts.edges?.map((edge) => (
                      <>
                        <Box key={edge.node.uuid}>
                          <Box>
                            {edge.node.firstName} {edge.node.lastName}
                          </Box>
                          <Box>
                            {edge.node.contacts?.find(
                              (contact) => contact?.type === ContactType.Email
                            )?.value || ''}
                          </Box>
                          <Box>
                            {formatPhoneDisplay(
                              edge.node.contacts?.find(
                                (contact) => contact?.type === ContactType.Phone
                              )?.value || ''
                            )}
                          </Box>
                        </Box>
                        <Box className="edit-delete-container-additional">
                          <Box
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              handleEditAdditional(
                                edge.node,
                                claimUuid,
                                ClaimContactType.Brokerage
                              );
                            }}
                          >
                            EDIT
                          </Box>
                          <Box
                            style={{ cursor: 'pointer', marginLeft: '10px' }}
                            onClick={() => {
                              if (edge.node.uuid) {
                                handleDeleteAdditionalInfo(edge.node.uuid);
                              }
                            }}
                          >
                            DELETE
                          </Box>
                        </Box>
                      </>
                    ))}
                  </Box>
                </Box>
                <Box
                  className="add-information"
                  onClick={() => {
                    setSelectedEntityForAdditionalInfo({
                      uuid: brokerEntity?.node.uuid || '',
                      type: ClaimContactType.Brokerage,
                    });
                    setIsAdditionalInfoModalOpen(true);
                  }}
                >
                  <AddCircleOutlineIcon />

                  <Typography variant="h6" className="add-text-btn">
                    ADD ADDITIONAL INFORMATION
                  </Typography>
                </Box>
              </ContactCard>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={isOthersOpen} onChange={handleOthersAccordion}>
          <AccordionSummary>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isOthersOpen ? (
                <KeyboardArrowDownIcon />
              ) : (
                <KeyboardArrowRightIcon />
              )}
              <Typography variant="h5" className="box-title">
                ADDITIONAL CONTACTS
              </Typography>
            </Box>
          </AccordionSummary>

          <AccordionDetails className="accordion-details-style insured-accordion-container">
            {otherContact && otherContact.length > 0 ? (
              otherContact.map((contact, index) => (
                <Box
                  className="insured-cards-wrapper"
                  style={{ marginBottom: '20px' }}
                >
                  <ContactCard className="contact-card-style-additional left-card">
                    <Box className="additional-content">
                      <Typography
                        variant="h6"
                        className="card-title"
                        style={{ marginTop: '25px' }}
                      >
                        {contact.companyName || ''}
                      </Typography>
                      <Typography variant="h6" className="card-title">
                        {contact?.firstName || ''} {contact?.lastName || ''}
                      </Typography>

                      <>
                        <Typography variant="h6" className="card-title">
                          {contact?.addresses &&
                          contact?.addresses[0]?.street1?.trim()
                            ? `${contact?.addresses[0]?.street1?.trim()}`
                            : ''}
                          {contact?.addresses && contact?.addresses[0]?.street2
                            ? `, ${
                                contact?.addresses &&
                                contact?.addresses[0]?.street2
                              }`
                            : ''}
                        </Typography>
                        <Typography variant="h6" className="card-title">
                          {contact?.addresses && contact?.addresses[0]?.city
                            ? `${contact?.addresses[0]?.city}`
                            : ''}
                          {contact?.addresses &&
                          contact?.addresses[0]?.regions?.edges?.[0]?.node
                            ?.iso2Code
                            ? contact?.addresses[0]?.city
                              ? `, ${contact?.addresses[0]?.regions?.edges?.[0]?.node?.iso2Code
                                  ?.split('-')
                                  .pop()}`
                              : contact?.addresses[0]?.regions?.edges?.[0]?.node?.iso2Code
                                  ?.split('-')
                                  .pop()
                            : ''}

                          {contact?.addresses &&
                          contact?.addresses[0]?.postalCode
                            ? contact?.addresses[0]?.regions?.edges?.[0]?.node
                                ?.iso2Code
                              ? `, ${contact?.addresses[0]?.postalCode}`
                              : contact?.addresses[0]?.postalCode
                            : ''}
                          {contact?.addresses &&
                          contact?.addresses[0]?.countries?.edges?.[0]?.node
                            ?.iso2Code &&
                          (contact?.addresses[0]?.postalCode?.trim() ||
                            contact?.addresses[0]?.street1?.trim() ||
                            contact?.addresses[0]?.street2?.trim() ||
                            contact?.addresses[0]?.city?.trim() ||
                            contact?.addresses[0]?.regions?.edges?.[0]?.node
                              ?.iso2Code)
                            ? `, ${contact?.addresses[0]?.countries?.edges?.[0]?.node?.iso2Code}`
                            : ''}
                        </Typography>
                      </>

                      <>
                        <Typography
                          variant="h6"
                          className="card-title"
                          style={{ marginTop: '10px' }}
                        >
                          {contact?.contacts?.find(
                            (contact) => contact?.type === ContactType.Email
                          )?.value || ''}
                        </Typography>
                        <Typography
                          variant="h6"
                          className="card-title"
                          style={{ paddingBottom: '30px' }}
                        >
                          {formatPhoneDisplay(
                            contact?.contacts?.find(
                              (contact) => contact?.type === ContactType.Phone
                            )?.value
                          )}
                        </Typography>
                      </>
                    </Box>

                    <Box className="edit-delete-container">
                      <Box
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          handleEditContact(
                            contact as ClaimantContact,
                            ClaimantType.ClaimContact as ClaimantType
                          );
                        }}
                      >
                        EDIT
                      </Box>
                      <Box
                        style={{ cursor: 'pointer', marginLeft: '10px' }}
                        onClick={() =>
                          handleDeleteClaimant(
                            contact.uuid as string,
                            'Additional Contact'
                          )
                        }
                      >
                        DELETE
                      </Box>
                    </Box>
                  </ContactCard>
                  <ContactCard className="contact-card-style-additional right-card">
                    <Box className="contact-card-header">
                      Additional Information
                      <Box className="additiona-Info-contact-page">
                        {contact.claimContacts?.map((edge) => (
                          <>
                            <Box key={edge.uuid}>
                              <Box>
                                {edge.firstName} {edge.lastName}
                              </Box>
                              <Box>
                                {edge.contacts?.find(
                                  (contact) =>
                                    contact?.type === ContactType.Email
                                )?.value || ''}
                              </Box>
                              <Box>
                                {formatPhoneDisplay(
                                  edge.contacts?.find(
                                    (contact) =>
                                      contact?.type === ContactType.Phone
                                  )?.value || ''
                                )}
                              </Box>
                            </Box>
                            <Box className="edit-delete-container-additional">
                              <Box
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  handleEditAdditional(
                                    edge,
                                    claimUuid,
                                    ClaimContactType.AdditionalContact
                                  );
                                }}
                              >
                                EDIT
                              </Box>
                              <Box
                                style={{
                                  cursor: 'pointer',
                                  marginLeft: '10px',
                                }}
                                onClick={() => {
                                  if (edge.uuid) {
                                    handleDeleteAdditionalInfo(edge.uuid);
                                  }
                                }}
                              >
                                DELETE
                              </Box>
                            </Box>
                          </>
                        ))}
                      </Box>
                    </Box>
                    <Box
                      className="add-information"
                      onClick={() => {
                        setSelectedEntityForAdditionalInfo({
                          uuid: contact.uuid || '',
                          type: ClaimContactType.AdditionalContact,
                        });
                        setIsAdditionalInfoModalOpen(true);
                      }}
                    >
                      <AddCircleOutlineIcon />

                      <Typography variant="h6" className="add-text-btn">
                        ADD ADDITIONAL INFORMATION
                      </Typography>
                    </Box>
                  </ContactCard>
                </Box>
              ))
            ) : (
              <Box
                className="insured-cards-wrapper"
                style={{ marginBottom: '20px' }}
              >
                <ContactCard className="contact-card-style left-card">
                  <Box
                    className="no-claimant-vendor"
                    style={{ marginTop: '20px', fontSize: '30px' }}
                  >
                    No additional contacts have been added
                  </Box>
                </ContactCard>
                <ContactCard className="contact-card-style right-card">
                  <Box
                    className="contact-card-header"
                    style={{ color: 'grey' }}
                  >
                    Additional Information
                  </Box>
                  <Box className="add-information">
                    <AddCircleOutlineIcon style={{ color: 'grey' }} />

                    <Typography
                      variant="h6"
                      className="add-text-btn"
                      style={{ color: 'grey' }}
                    >
                      ADD ADDITIONAL INFORMATION
                    </Typography>
                  </Box>
                </ContactCard>
              </Box>
            )}

            <Box
              className="add-card"
              style={{ marginLeft: '10px', marginTop: '20px' }}
              onClick={() => {
                setIsContactModalOpen(true);
                setTypeOfContact(ClaimantType.ClaimContact);
              }}
              sx={{
                cursor: 'pointer',
                color: 'black',
                pointerEvents: 'auto',
              }}
            >
              <AddCircleOutlineIcon />
              <Typography variant="h6" className="add-text-btn">
                ADD CONTACT
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
      </ClaimEntityPageContainer>
    </>
  );
};
